'use client';

import Image from '@/components/Image';
import { useAppNavigation } from '@/hooks/useAppNavigation';
import { useStore } from '@/providers/app-store-provider';
import { EPlatform } from '@/utils/enums/platform-enum';
import { formatPriceNumberWithSymbol, priceInfo } from '@/utils/price';
import { useEffect, useRef, useState } from 'react';
import { Autoplay } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import styles from './DivaRadiant2.module.css';

interface RadiantProduct {
  id: number;
  shop_id?: number;
  shop_sku: string;
  name: string;
  short_name: string;
  price_info: any;
  price_info_v1?: any;
  price_info_v2?: any;
  price_info_v3?: any;
  discount_percent_v1?: number;
  discount_percent_v2?: number;
  discount_percent_v3?: number;
  image: {
    product?: string[];
  };
}

interface RadiantItem {
  link: string;
  sell_price: string;
  shop_sku: string;
  name: string;
}

interface RadiantImageItem {
  url: string;
  shop_sku: string;
  name: string;
}

interface DivaRadiant2Props {
  pageData: {
    radiant2: {
      list: RadiantProduct[];
      img_list: string[];
      group_id: number;
      title_image?: string; // WAP端标题图片在radiant2对象内
    };
    radiant2_title_image?: string; // PC端标题图片在根级别
  };
  deviceType: EPlatform;
}

export default function DivaRadiant2({ pageData, deviceType }: DivaRadiant2Props) {
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);
  const { navigate } = useAppNavigation();

  const isWap = deviceType !== EPlatform.WEB;

  // 双轮播引用
  const mainSwiperRef = useRef<any>(null);
  const thumbSwiperRef = useRef<any>(null);

  // 状态管理
  const [activeIndex, setActiveIndex] = useState(0);
  const [slideFige, setSlideFige] = useState(false);
  const [wapActiveIndex, setWapActiveIndex] = useState(0); // WAP端底部轮播活跃索引

  const radiant2Data = pageData.radiant2;

  // 使用公共的价格处理方法
  const getPriceInfo = (item: RadiantProduct) => {
    return priceInfo(userInfo, item, currency);
  };

  // 获取商品展示图片
  const getProductImage = (item: RadiantProduct): string => {
    if (item.image?.product && item.image.product.length > 0) {
      return item.image.product[0];
    }

    return '';
  };

  // 处理主轮播数据（结合配置图片和商品详情）
  const getMainSlideData = (): RadiantItem[] => {
    const { list = [], img_list = [] } = radiant2Data;
    const arr: RadiantItem[] = [];
    const length = Math.min(list.length, img_list.length);

    for (let i = 0; i < length; i++) {
      const priceData = getPriceInfo(list[i]);
      arr.push({
        link: img_list[i],
        sell_price: priceData.sell_price || '0',
        shop_sku: list[i].shop_sku,
        name: list[i].name,
      });
    }

    return arr;
  };

  // 处理缩略图数据
  const getThumbData = (): RadiantImageItem[] => {
    const { list = [] } = radiant2Data;
    const thumbs = list.map((item) => ({
      url: getProductImage(item),
      shop_sku: item.shop_sku,
      name: item.name,
    }));

    return thumbs;
  };

  const mainSlideData = getMainSlideData();
  const thumbData = getThumbData();

  // 获取标题图片 - 根据设备类型使用不同字段
  const getTitleImage = (): string => {
    if (isWap) {
      // WAP端: pageData.radiant2.title_image
      return pageData.radiant2?.title_image || '';
    } else {
      // PC端: pageData.radiant2_title_image
      return pageData.radiant2_title_image || '';
    }
  };

  const titleImage = getTitleImage();

  // 商品点击处理
  const handleProductClick = (item: RadiantItem | RadiantImageItem) => {
    navigate(`/goods-detail/${item.shop_sku}`);
  };

  // 暴露给外部的方法（模拟Vue的$refs调用）
  const updateSlides = () => {
    // Vue版本的updateSlides方法：只操作底部缩略图轮播
    if (thumbSwiperRef.current?.swiper) {
      thumbSwiperRef.current.swiper.slideTo(0, 0, false);
    }
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).divaRadiant2UpdateSlides = updateSlides;
    }

    return () => {
      if (typeof window !== 'undefined') {
        delete (window as any).divaRadiant2UpdateSlides;
      }
    };
  }, []);

  // PC端自动播放监控机制
  useEffect(() => {
    if (!isWap && mainSwiperRef.current?.swiper) {
      const { swiper } = mainSwiperRef.current;

      // 添加一个定时器，每隔10秒检查自动播放状态
      const autoplayMonitor = setInterval(() => {
        if (swiper.autoplay && !swiper.autoplay.running) {
          swiper.autoplay.start();
        }
      }, 10000);

      return () => {
        clearInterval(autoplayMonitor);
      };
    }
  }, [isWap, mainSwiperRef.current]);

  // WAP端自动播放监控机制（监控主轮播）
  useEffect(() => {
    if (isWap && mainSwiperRef.current?.swiper) {
      const { swiper } = mainSwiperRef.current;

      // 添加一个定时器，每隔10秒检查自动播放状态
      const autoplayMonitor = setInterval(() => {
        if (swiper.autoplay && !swiper.autoplay.running) {
          swiper.autoplay.start();
        }
      }, 10000);

      return () => {
        clearInterval(autoplayMonitor);
      };
    }
  }, [isWap, mainSwiperRef.current]);

  // WAP端初始化：确保初始状态正确
  useEffect(() => {
    if (isWap) {
      setWapActiveIndex(0);
    }
  }, [isWap]);

  // 安全检查
  if (!pageData.radiant2?.list || pageData.radiant2.list.length === 0) {
    return null;
  }

  // 检查是否有图片列表
  if (!pageData.radiant2?.img_list || pageData.radiant2.img_list.length === 0) {
    return null;
  }

  if (isWap) {
    // 移动端布局 - 基于WAP版本Radiant2.vue
    return (
      <div className="diva-radiant2 bg-black">
        {titleImage && (
          <Image
            className="title-image w-[375px]"
            src={titleImage}
            alt="Keep exploring the sparking glasses showcase your full charm banner2"
            width={375}
            height={200}
          />
        )}

        <div className="photo-swiper w-[345px] h-[425px] mx-auto relative">
          {/* 主轮播 */}
          <Swiper
            ref={mainSwiperRef}
            modules={[Autoplay]}
            spaceBetween={35}
            slidesPerView={1}
            slidesPerGroup={1}
            autoplay={{
              delay: 3000,
              stopOnLastSlide: false,
              disableOnInteraction: false,
              pauseOnMouseEnter: false,
              reverseDirection: false,
            }}
            onAutoplayPause={(swiper) => {
              // 当自动播放被暂停时，延时后重新启动
              setTimeout(() => {
                if (swiper.autoplay && typeof swiper.autoplay.start === 'function') {
                  swiper.autoplay.start();
                }
              }, 1000);
            }}
            onTouchStart={() => {
              // Vue版本的touchStart逻辑
              setSlideFige(true);
            }}
            onSlideChange={(swiper) => {
              const currentIndex = swiper.activeIndex;

              // 更新活跃索引
              setWapActiveIndex(currentIndex);

              // 主轮播变化时，底部轮播跟随并居中显示选中项
              if (thumbSwiperRef.current?.swiper) {
                // 使用slideTo with runCallbacks=true来确保选中项居中
                thumbSwiperRef.current.swiper.slideTo(currentIndex, 300, true);
              }

              // WAP端特殊逻辑：到达最后一张时，延时后跳回第一张
              if (currentIndex === mainSlideData.length - 1) {
                setTimeout(() => {
                  // 先停止自动播放
                  if (swiper.autoplay && typeof swiper.autoplay.stop === 'function') {
                    swiper.autoplay.stop();
                  }

                  // 跳转到第一张
                  swiper.slideTo(0, 0, false);
                  if (thumbSwiperRef.current?.swiper) {
                    thumbSwiperRef.current.swiper.slideTo(0, 300, true); // 居中显示第一张
                  }
                  setWapActiveIndex(0);

                  // 重新启动自动播放
                  setTimeout(() => {
                    if (swiper.autoplay && typeof swiper.autoplay.start === 'function') {
                      swiper.autoplay.start();
                    }
                  }, 100);
                }, 2500);
              }

              // 如果是手动滑动，重置标志
              if (slideFige) {
                setSlideFige(false);
              }
            }}
            className="w-full h-full"
          >
            {mainSlideData.map((item, index) => (
              <SwiperSlide key={index} className="photo-item w-full relative">
                <a
                  href={`/goods-detail/${item.shop_sku}`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleProductClick(item);
                  }}
                >
                  <Image
                    src={item.link}
                    alt={`${item.name}1`}
                    width={345}
                    height={425}
                    className="w-full h-full object-cover"
                  />
                  <div className="buy-price absolute right-[15px] bottom-[110px]">
                    <div className="buy-box w-[215px] h-[35px] bg-white rounded-[18px] border border-black relative box-border">
                      <div className="price font-bb text-[15px] h-[35px] text-black flex items-center pl-[16px] notranslate">
                        {formatPriceNumberWithSymbol(item.sell_price, currency)}
                      </div>
                      <div className="buy-btn flex items-center justify-center w-[100px] h-[35px] bg-black rounded-[18px] absolute right-[-1px] top-0 bottom-0 font-br text-[12px] text-white">
                        BUY NOW
                      </div>
                    </div>
                  </div>
                </a>
              </SwiperSlide>
            ))}
          </Swiper>

          {/* 底部商品缩略图轮播 - 与Vue版本保持完全一致 */}
          <div
            className={`product-swiper w-[345px] absolute z-[9] bottom-[14px] pl-[10px] box-border ${styles['wap-product-swiper']}`}
          >
            <Swiper
              ref={thumbSwiperRef}
              slidesPerView="auto"
              slidesPerGroup={1}
              slidesOffsetBefore={10}
              // 底部轮播不自动播放，只跟随主轮播
              onClick={(swiper, event) => {
                // 点击缩略图切换主轮播逻辑
                const target = event.target as HTMLElement;
                const itemData = target.getAttribute('data-item');
                if (itemData) {
                  try {
                    const data = JSON.parse(itemData) as RadiantImageItem;
                    const index = thumbData.findIndex((item) => item.shop_sku === data.shop_sku);
                    if (index >= 0) {
                      // 直接切换主轮播，底部轮播会在onSlideChange中跟随并居中
                      if (mainSwiperRef.current?.swiper) {
                        mainSwiperRef.current.swiper.slideTo(index, 300, false);
                      }
                      // 状态更新会在主轮播的onSlideChange中处理
                    }
                  } catch {
                    // 忽略JSON解析错误
                  }
                }
              }}
              className={`h-full ${styles['wap-product-swiper']}`}
            >
              {thumbData.map((item, index) => (
                <SwiperSlide
                  key={index}
                  className={`product-item h-[80px] !w-[80px] mx-[5px]`}
                  style={{
                    // 根据活跃状态应用缩放效果
                    transform: wapActiveIndex === index ? 'scale(1.1)' : 'scale(1.0)',
                    transformOrigin: 'bottom center',
                    transition: 'all 0.4s ease',
                  }}
                >
                  <div
                    className="product-image w-[80px] h-[80px] bg-white rounded-[8px] box-border flex items-center justify-center wap-product-image"
                    data-item={JSON.stringify(item)}
                  >
                    <Image
                      src={item.url}
                      alt={`${item.name}2`}
                      width={65}
                      height={65}
                      className="w-[65px]"
                      data-item={JSON.stringify(item)}
                    />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      </div>
    );
  }

  // PC端布局 - 基于PC版本Radiant2.vue
  return (
    <div className="like-dianond bg-black pt-[1.56vw]">
      {titleImage && (
        <Image
          className="title-image w-full"
          src={titleImage}
          alt="Keep exploring the sparking glasses showcase your full charm banner2"
          width={1920}
          height={400}
        />
      )}

      <div className="swiper-box relative w-[87.5vw] h-[29.17vw] mx-auto overflow-hidden">
        {/* 主轮播 - 垂直方向 */}
        <Swiper
          ref={mainSwiperRef}
          modules={[Autoplay]}
          direction="vertical"
          slidesPerGroup={1}
          loop={false} // PC端不循环，切到最后一个再滚到第一个
          autoplay={{
            delay: 3000,
            stopOnLastSlide: false, // 不在最后一张停止
            disableOnInteraction: false, // 用户交互后继续自动播放
            pauseOnMouseEnter: false, // 鼠标悬停时不暂停
            reverseDirection: false, // 不反向播放
          }}
          allowTouchMove // 允许手动滑动
          onAutoplayPause={(swiper) => {
            // 当自动播放被暂停时，延时后重新启动
            setTimeout(() => {
              if (swiper.autoplay && typeof swiper.autoplay.start === 'function') {
                swiper.autoplay.start();
              }
            }, 1000);
          }}
          onSlideChange={(swiper) => {
            // Vue版本的PC端交互逻辑：一直自动切换，不循环
            const { realIndex } = swiper;
            if (realIndex >= 0) {
              // 同步右侧缩略图轮播
              if (thumbSwiperRef.current?.swiper) {
                thumbSwiperRef.current.swiper.slideToLoop(realIndex, 1000, false);
              }
              setActiveIndex(realIndex);

              // 到达最后一张时，延时后回到第一张（不是循环）
              if (realIndex === mainSlideData.length - 1) {
                setTimeout(() => {
                  // 先停止自动播放
                  if (swiper.autoplay && typeof swiper.autoplay.stop === 'function') {
                    swiper.autoplay.stop();
                  }

                  // 跳转到第一张 - 瞬间切换，无过渡动画
                  swiper.slideTo(0, 0, false); // 0ms过渡时间，直接跳转
                  if (thumbSwiperRef.current?.swiper) {
                    thumbSwiperRef.current.swiper.slideToLoop(0, 0, false);
                  }
                  setActiveIndex(0);

                  // 延时后重新启动自动播放，确保持续循环
                  setTimeout(() => {
                    if (swiper.autoplay && typeof swiper.autoplay.start === 'function') {
                      swiper.autoplay.start();
                    }
                  }, 100); // 瞬间跳转后快速重启
                }, 2500);
              }
            }
          }}
          className="product-swiper w-[87.5vw] h-[29.17vw]"
        >
          {mainSlideData.map((item, index) => (
            <SwiperSlide
              key={index}
              className="swiper-slide-item relative w-[87.5vw] h-[29.17vw] inline-block"
            >
              <a
                href={`/goods-detail/${item.shop_sku}`}
                onClick={(e) => {
                  e.preventDefault();
                  handleProductClick(item);
                }}
              >
                <Image
                  src={item.link}
                  alt={`${item.name}1`}
                  width={1680}
                  height={560}
                  className="w-[87.5vw] h-[29.17vw] object-cover"
                />
                <div className="bay-now-box flex items-center absolute bottom-[2.08vw] right-[10.42vw] w-[22.5vw] h-[3.65vw] bg-white rounded-[1.82vw] border-2 border-black box-border">
                  <div className="price font-bb text-[1.56vw] text-black leading-[0.52vw] pl-[1.77vw] notranslate">
                    {formatPriceNumberWithSymbol(item.sell_price, currency)}
                  </div>
                  <div className="buy-now-btn flex items-center justify-center absolute box-border border-2 border-black right-[-1px] top-[-1px] bottom-0 w-[10.52vw] h-[3.65vw] text-[1.25vw] bg-black rounded-[1.82vw] font-br text-white z-[2]">
                    BUY NOW
                  </div>
                </div>
              </a>
            </SwiperSlide>
          ))}
        </Swiper>

        {/* 右侧缩略图导航 - 与Vue版本位置和样式完全一致 */}
        <div
          className={`product-nav-right absolute top-[1.56vw] right-[1.98vw] h-[calc(100%-1.56vw)] text-right z-[99] box-border ${
            mainSlideData.length > 4 ? styles['loop-swiper-slide'] : styles['no-loop-swiper-slide']
          }`}
        >
          <Swiper
            ref={thumbSwiperRef}
            modules={mainSlideData.length > 4 ? [Autoplay] : []}
            direction="vertical"
            slidesPerView={4.5}
            slidesPerGroup={1}
            loop={mainSlideData.length > 4}
            autoplay={
              mainSlideData.length > 4
                ? {
                    delay: 3000,
                    stopOnLastSlide: false,
                    disableOnInteraction: false,
                  }
                : false
            }
            onSlideChange={(swiper) => {
              // Vue版本的rightOption联动逻辑
              if (mainSlideData.length > 4) {
                const { realIndex } = swiper;
                if (realIndex >= 0 && mainSwiperRef.current?.swiper) {
                  mainSwiperRef.current.swiper.slideToLoop(realIndex, 0, false);
                }
              } else {
                const currentActiveIndex = swiper.activeIndex;
                if (mainSwiperRef.current?.swiper) {
                  mainSwiperRef.current.swiper.slideTo(currentActiveIndex, 200, false);
                }
              }
            }}
            onClick={(swiper, event) => {
              // Vue版本的点击逻辑
              const target = event.target as HTMLElement;
              const indexAttr = target.getAttribute('data-index');
              if (indexAttr) {
                const itemIndex = parseInt(indexAttr, 10);
                if (mainSlideData.length <= 4) {
                  setActiveIndex(itemIndex);
                  if (mainSwiperRef.current?.swiper) {
                    mainSwiperRef.current.swiper.slideTo(itemIndex, 0, false);
                  }
                } else if (itemIndex >= 0 && mainSwiperRef.current?.swiper) {
                  mainSwiperRef.current.swiper.slideTo(itemIndex, 0, false);
                }
              }
            }}
            className="pro-imgs h-full text-right !pl-[30px]"
          >
            {thumbData.map((item, index) => (
              <SwiperSlide
                key={index}
                className={activeIndex === index && mainSlideData.length <= 4 ? styles.actived : ''}
                style={{
                  // 对于超过4个项目的情况，确保有正确的过渡动画
                  ...(mainSlideData.length > 4 && {
                    transition: 'all 0.4s',
                  }),
                }}
              >
                <div
                  className={`right-img-item w-[5.78vw] h-[5.78vw] bg-white rounded-[0.78vw] flex items-center justify-center py-[2.19vw] px-[0.73vw] box-border cursor-pointer transition-all duration-200`}
                  data-item={JSON.stringify(item)}
                  data-index={index.toString()}
                  style={{
                    // 强制应用缩放效果和间距
                    ...(activeIndex === index &&
                      mainSlideData.length <= 4 && {
                        transform: 'scale(1.2) !important',
                        transformOrigin: 'right center !important',
                        transition: 'all 0.2s !important',
                        zIndex: 10,
                      }),
                  }}
                >
                  {item.url ? (
                    <Image
                      src={item.url}
                      alt={`${item.name}2`}
                      width={111}
                      height={111}
                      className="w-full object-contain" // Vue版本: width: 100%
                      data-item={JSON.stringify(item)}
                      data-index={index.toString()}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                      No Image
                    </div>
                  )}
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </div>
  );
}
